import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

// Active content locale (set synchronously by LanguageProvider). Used to request
// translated content from public GET endpoints across the whole app.
let _apiLocale = "en";
export const setApiLocale = (loc) => { _apiLocale = loc || "en"; };

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("mlv_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const method = (config.method || "get").toLowerCase();
  if (method === "get" && _apiLocale && _apiLocale !== "en") {
    const params = config.params || {};
    if (params.locale == null) config.params = { ...params, locale: _apiLocale };
  }
  return config;
});

export default api;

// ---- Public ----
export const getStats = () => api.get("/stats").then((r) => r.data);
export const searchVillas = (params) => api.get("/villas", { params }).then((r) => r.data);
export const getVilla = (slug, locale) => api.get(`/villas/${slug}`, { params: locale && locale !== "en" ? { locale } : {} }).then((r) => r.data);
export const getSimilar = (slug) => api.get(`/villas/${slug}/similar`).then((r) => r.data);
export const getAreas = () => api.get("/areas").then((r) => r.data);
export const getArea = (slug, locale) => api.get(`/areas/${slug}`, { params: locale && locale !== "en" ? { locale } : {} }).then((r) => r.data);
export const getMapVillas = () => api.get("/map/villas").then((r) => r.data);
export const getReviews = (params) => api.get("/reviews", { params }).then((r) => r.data);
export const createReview = (body) => api.post("/reviews", body).then((r) => r.data);
export const getBlog = (params, locale) => api.get("/blog", { params: { ...(params || {}), ...(locale && locale !== "en" ? { locale } : {}) } }).then((r) => r.data);
export const getBlogPost = (slug, locale) => api.get(`/blog/${slug}`, { params: locale && locale !== "en" ? { locale } : {} }).then((r) => r.data);
export const getSeoPages = (params) => api.get("/seo-pages", { params }).then((r) => r.data);
export const getSeoPage = (slug, locale) => api.get(`/seo-pages/${slug}`, { params: locale && locale !== "en" ? { locale } : {} }).then((r) => r.data);
export const sendContact = (body) => api.post("/contact", body).then((r) => r.data);
export const validateCoupon = (code) => api.get(`/coupons/${code}/validate`).then((r) => r.data);
export const getPublicSettings = () => api.get("/settings/public").then((r) => r.data);

// ---- Booking ----
export const createBooking = (body) => api.post("/booking-requests", body).then((r) => r.data);
export const getBooking = (id) => api.get(`/booking-requests/${id}`).then((r) => r.data);
export const lookupBooking = (body) => api.post("/booking-lookup", body).then((r) => r.data);
export const myBookings = () => api.get("/my/bookings").then((r) => r.data);
export const myInvoices = () => api.get("/my/invoices").then((r) => r.data);
export const cancelBooking = (id) => api.post(`/booking-requests/${id}/cancel`).then((r) => r.data);
export const invoicePdfUrl = (id) => `${API}/invoices/${id}/pdf`;
export const voucherPdfUrl = (id) => `${API}/booking-requests/${id}/voucher.pdf`;

// ---- AI ----
export const aiConcierge = (body) => api.post("/ai/concierge", body).then((r) => r.data);
export const aiRecommend = (query) => api.post("/ai/recommend", { query }).then((r) => r.data);
export const aiSearch = (query) => api.post("/ai/search", { query }).then((r) => r.data);
export const aiSeoGenerate = (keyword) => api.post("/ai/seo-generate", { keyword }).then((r) => r.data);
export const aiFaqGenerate = (context, count) => api.post("/ai/faq-generate", { context, count }).then((r) => r.data);

// ---- Auth ----
export const apiLogin = (body) => api.post("/auth/login", body).then((r) => r.data);
export const apiRegister = (body) => api.post("/auth/register", body).then((r) => r.data);
export const apiMe = () => api.get("/auth/me").then((r) => r.data);
export const updateProfile = (body) => api.put("/auth/profile", body).then((r) => r.data);
export const getFavorites = () => api.get("/auth/favorites").then((r) => r.data);
export const toggleFavoriteApi = (villaId) => api.post(`/auth/favorites/${villaId}`).then((r) => r.data);

// ---- Admin ----
export const adminAnalytics = () => api.get("/admin/analytics").then((r) => r.data);
export const adminBookings = (status) => api.get("/admin/bookings", { params: status ? { status } : {} }).then((r) => r.data);
export const adminUpdateBooking = (id, body) => api.put(`/admin/bookings/${id}`, body).then((r) => r.data);
export const adminCreateInvoice = (id) => api.post(`/admin/bookings/${id}/invoice`).then((r) => r.data);
export const adminCrm = () => api.get("/admin/crm").then((r) => r.data);
export const adminCoupons = () => api.get("/admin/coupons").then((r) => r.data);
export const adminCreateCoupon = (body) => api.post("/admin/coupons", body).then((r) => r.data);
export const adminDeleteCoupon = (id) => api.delete(`/admin/coupons/${id}`).then((r) => r.data);
export const adminReviews = () => api.get("/admin/reviews").then((r) => r.data);
export const adminModerateReview = (id, body) => api.put(`/admin/reviews/${id}`, body).then((r) => r.data);
export const adminDeleteReview = (id) => api.delete(`/admin/reviews/${id}`).then((r) => r.data);
export const adminVillaCreate = (body) => api.post("/admin/villas", body).then((r) => r.data);
export const adminVillaUpdate = (id, body) => api.put(`/admin/villas/${id}`, body).then((r) => r.data);
export const adminVillaDelete = (id) => api.delete(`/admin/villas/${id}`).then((r) => r.data);
export const adminVillaBulk = (body) => api.post("/admin/villas/bulk", body).then((r) => r.data);
export const adminVillaCalendar = (id, blocked) => api.put(`/admin/villas/${id}/calendar`, { blocked_dates: blocked }).then((r) => r.data);
export const adminVillaCalendarGet = (id) => api.get(`/admin/villas/${id}/calendar`).then((r) => r.data);
export const adminVillaIcalSave = (id, ical_urls) => api.put(`/admin/villas/${id}/ical`, { ical_urls }).then((r) => r.data);
export const adminVillaIcalSync = (id) => api.post(`/admin/villas/${id}/ical/sync`).then((r) => r.data);

// ---- Programmatic SEO ----
export const adminSeoStatus = () => api.get("/admin/seo/status").then((r) => r.data);
export const adminSeoGenerate = (body) => api.post("/admin/seo/generate", body || {}).then((r) => r.data);
export const adminSeoEnrich = (body) => api.post("/admin/seo/enrich", body || {}).then((r) => r.data);

// ---- IndexNow (instant indexing) ----
export const adminIndexNowConfig = () => api.get("/admin/indexnow/config").then((r) => r.data);
export const adminIndexNowUpdateConfig = (body) => api.put("/admin/indexnow/config", body).then((r) => r.data);
export const adminIndexNowVerify = () => api.post("/admin/indexnow/verify").then((r) => r.data);
export const adminIndexNowSubmit = (body) => api.post("/admin/indexnow/submit", body).then((r) => r.data);
export const adminIndexNowSubmitAll = () => api.post("/admin/indexnow/submit-all").then((r) => r.data);
export const adminIndexNowRegenerateKey = () => api.post("/admin/indexnow/regenerate-key").then((r) => r.data);
export const adminIndexNowPreviewUrls = () => api.get("/admin/indexnow/preview-urls").then((r) => r.data);
export const adminIndexNowLogs = (limit = 25) => api.get("/admin/indexnow/logs", { params: { limit } }).then((r) => r.data);

// ---- Owner ----
export const ownerDashboard = () => api.get("/owner/dashboard").then((r) => r.data);
export const ownerVillaCalendarGet = (id) => api.get(`/owner/villas/${id}/calendar`).then((r) => r.data);
export const ownerVillaCalendar = (id, blocked) => api.put(`/owner/villas/${id}/calendar`, { blocked_dates: blocked }).then((r) => r.data);
export const ownerVillaIcalSave = (id, ical_urls) => api.put(`/owner/villas/${id}/ical`, { ical_urls }).then((r) => r.data);
export const ownerVillaIcalSync = (id) => api.post(`/owner/villas/${id}/ical/sync`).then((r) => r.data);

// ---- Admin: Settings / Integrations ----
export const adminGetSettings = () => api.get("/admin/settings").then((r) => r.data);
export const adminUpdateSettings = (body) => api.put("/admin/settings", body).then((r) => r.data);
export const adminTelegramTest = () => api.post("/admin/settings/telegram-test", {}).then((r) => r.data);

// ---- Admin: Translations ----
export const adminTranslationStatus = () => api.get("/admin/translations/status").then((r) => r.data);
export const adminGenerateAllTranslations = (body) => api.post("/admin/translations/generate-all", body || {}).then((r) => r.data);
export const adminGenerateTranslation = (body) => api.post("/admin/translations/generate", body).then((r) => r.data);
export const adminTranslationEntities = (entity_type) => api.get("/admin/translations/entities", { params: { entity_type } }).then((r) => r.data);
export const adminGetTranslations = (entity_type, entity_id) => api.get("/admin/translations", { params: { entity_type, entity_id } }).then((r) => r.data);
export const adminSaveTranslation = (body) => api.put("/admin/translations", body).then((r) => r.data);
